/**
 * Upload-Bereich im Tab „Medien & Dokumente": Drag & Drop + Dateiauswahl,
 * mehrere Dateien, Kategorie je Datei (Medium/Dokument wird am Dateityp
 * erkannt, ist bei Bildern aber über die Kategorie umstellbar), Fortschritt
 * je Datei. Hochgeladen wird Datei für Datei (eigener Fortschritt + Fehler).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../../../components/ui';
import { BRAND, BRAND_25, BRAND_LIGHT, getThemeColors } from '../../../../lib/theme';
import { useIsMobile } from '../../../../lib/useBreakpoint';
import {
  allowedKinds,
  defaultCategory,
  detectKind,
  formatFileSize,
  isImageFile,
  maxBytesFor,
  mediaDocErrorMessage,
  mediaDocumentsApi,
  fileExtension,
  type MediaDocKind,
  type MediaDocMeta,
} from '../../../../lib/mediaDocuments';
import { categoryLabel, FileBadge } from './shared';

type StagedStatus = 'ready' | 'uploading' | 'done' | 'error' | 'invalid';

interface StagedFile {
  key: string;
  file: File;
  kinds: MediaDocKind[];
  kind: MediaDocKind;
  kategorie: string;
  status: StagedStatus;
  progress: number;
  error: string | null;
  previewUrl: string | null;
}

interface Props {
  userId: number;
  meta: MediaDocMeta;
  dark: boolean;
  /** Nach erfolgreichem Upload (mind. eine Datei). */
  onUploaded: () => void;
}

let keySeq = 0;

export function MediaUploadPanel({ userId, meta, dark, onUploaded }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const dragDepth = useRef(0);

  // Vorschau-URLs freigeben
  const stagedRef = useRef(staged);
  stagedRef.current = staged;
  useEffect(() => () => {
    stagedRef.current.forEach(s => { if (s.previewUrl) URL.revokeObjectURL(s.previewUrl); });
  }, []);

  const accept = useMemo(
    () => Array.from(new Set([...meta.accept.media, ...meta.accept.documents])).map(e => `.${e}`).join(','),
    [meta],
  );

  const addFiles = useCallback((list: FileList | File[] | null) => {
    if (!list) return;
    setNotice(null);
    const next: StagedFile[] = Array.from(list).map(file => {
      const kinds = allowedKinds(file, meta);
      let kind = detectKind(file);
      if (kinds.length && !kinds.includes(kind)) kind = kinds[0];
      let error: string | null = null;
      if (!kinds.length) {
        error = tr('pflege.media.error_type', { ext: fileExtension(file.name) || '?' });
      } else if (file.size > maxBytesFor(file, kind, meta)) {
        error = tr('pflege.media.error_size', { max: Math.round(maxBytesFor(file, kind, meta) / 1024 / 1024) });
      }
      const canPreview = isImageFile(file) && !/heic|heif/i.test(file.name) && typeof URL.createObjectURL === 'function';
      return {
        key: `f${++keySeq}`,
        file,
        kinds,
        kind,
        kategorie: defaultCategory(file, kind, meta),
        status: error ? 'invalid' : 'ready',
        progress: 0,
        error,
        previewUrl: canPreview ? URL.createObjectURL(file) : null,
      } as StagedFile;
    });
    setStaged(prev => [...prev, ...next]);
  }, [meta, tr]);

  const removeStaged = (key: string) => {
    setStaged(prev => {
      const hit = prev.find(s => s.key === key);
      if (hit?.previewUrl) URL.revokeObjectURL(hit.previewUrl);
      return prev.filter(s => s.key !== key);
    });
  };

  const clearAll = () => {
    staged.forEach(s => { if (s.previewUrl) URL.revokeObjectURL(s.previewUrl); });
    setStaged([]);
    setNotice(null);
  };

  const setCategory = (key: string, value: string) => {
    const [kind, slug] = value.split(':') as [MediaDocKind, string];
    setStaged(prev => prev.map(s => {
      if (s.key !== key) return s;
      const tooBig = s.file.size > maxBytesFor(s.file, kind, meta);
      return {
        ...s, kind, kategorie: slug,
        status: tooBig ? 'invalid' : (s.status === 'invalid' || s.status === 'error' ? 'ready' : s.status),
        error: tooBig ? tr('pflege.media.error_size', { max: Math.round(maxBytesFor(s.file, kind, meta) / 1024 / 1024) }) : null,
      };
    }));
  };

  const setCategoryForAll = (value: string) => {
    if (!value) return;
    const [kind] = value.split(':') as [MediaDocKind, string];
    staged.forEach(s => { if (s.kinds.includes(kind) && s.status !== 'invalid' && s.status !== 'done') setCategory(s.key, value); });
  };

  const patch = (key: string, p: Partial<StagedFile>) =>
    setStaged(prev => prev.map(s => (s.key === key ? { ...s, ...p } : s)));

  const startUpload = async () => {
    const queue = staged.filter(s => s.status === 'ready' || s.status === 'error');
    if (!queue.length) return;
    setBusy(true);
    setNotice(null);
    let ok = 0;
    for (const s of queue) {
      patch(s.key, { status: 'uploading', progress: 0, error: null });
      try {
        await mediaDocumentsApi.upload(userId, s.kind, [s.file], s.kategorie, p => patch(s.key, { progress: p }));
        patch(s.key, { status: 'done', progress: 100 });
        ok++;
      } catch (err) {
        patch(s.key, { status: 'error', error: mediaDocErrorMessage(err) ?? tr('pflege.media.upload_failed') });
      }
    }
    setBusy(false);
    if (ok > 0) {
      setNotice(tr('pflege.media.uploaded_success', { count: ok }));
      // Erfolgreiche Zeilen entfernen, fehlgeschlagene stehen lassen.
      setStaged(prev => {
        prev.forEach(s => { if (s.status === 'done' && s.previewUrl) URL.revokeObjectURL(s.previewUrl); });
        return prev.filter(s => s.status !== 'done');
      });
      onUploaded();
    }
  };

  const readyCount = staged.filter(s => s.status === 'ready' || s.status === 'error').length;
  const limits = meta.limits;

  const categoryOptions = (kinds: MediaDocKind[]) => kinds.map(kind => (
    <optgroup key={kind} label={tr(kind === 'media' ? 'pflege.media.title_media' : 'pflege.media.title_docs')}>
      {(kind === 'media' ? meta.categories.media : meta.categories.documents).map(slug => (
        <option key={`${kind}:${slug}`} value={`${kind}:${slug}`}>{categoryLabel(tr, kind, slug, meta.role)}</option>
      ))}
    </optgroup>
  ));

  const selectStyle: React.CSSProperties = {
    border: `1px solid ${t.border}`, borderRadius: 8, background: t.inputBg, color: t.heading,
    fontSize: 12.5, padding: '6px 8px', fontFamily: 'inherit', maxWidth: '100%',
  };

  return (
    <Card dark={dark} style={{ padding: 0 }}>
      <div
        data-testid="media-dropzone"
        onDragEnter={e => { e.preventDefault(); dragDepth.current++; setDragging(true); }}
        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
        onDragLeave={e => { e.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragging(false); }}
        onDrop={e => { e.preventDefault(); dragDepth.current = 0; setDragging(false); if (!busy) addFiles(e.dataTransfer.files); }}
        style={{
          margin: isMobile ? 12 : 16,
          border: `1.5px dashed ${dragging ? BRAND : t.border}`,
          background: dragging ? (dark ? '#2a1222' : BRAND_25) : t.cardMuted,
          borderRadius: 12,
          padding: isMobile ? '18px 14px' : '22px 20px',
          display: 'flex', alignItems: 'center', gap: 16,
          flexDirection: isMobile ? 'column' : 'row',
          textAlign: isMobile ? 'center' : 'left',
          transition: 'border-color 0.15s, background 0.15s',
        }}
      >
        <span aria-hidden style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: dark ? '#2a1222' : BRAND_LIGHT,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 16V4M7.5 8.5 12 4l4.5 4.5" /><path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
          </svg>
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: t.heading }}>
            {dragging ? tr('pflege.media.drop_now') : tr('pflege.media.upload_hint')}
          </div>
          <div style={{ fontSize: 12, color: t.muted, marginTop: 3, lineHeight: 1.45 }}>
            {tr('pflege.media.upload_sub', {
              image: Math.round(limits.image_max_kb / 1024),
              video: Math.round(limits.video_max_kb / 1024),
              doc: Math.round(limits.document_max_kb / 1024),
            })}
          </div>
        </div>
        <Button variant="secondary" size="sm" dark={dark} disabled={busy} onClick={() => inputRef.current?.click()}>
          {tr('pflege.media.choose_files')}
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          data-testid="media-file-input"
          aria-label={tr('pflege.media.choose_files')}
          style={{ display: 'none' }}
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {notice && (
        <div role="status" style={{ margin: '0 16px 12px', fontSize: 12.5, color: '#067647', fontWeight: 500 }}>✓ {notice}</div>
      )}

      {staged.length > 0 && (
        <div style={{ borderTop: `1px solid ${t.border}` }}>
          <div style={{
            padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            fontSize: 12, color: t.muted,
          }}>
            <span style={{ fontWeight: 600, color: t.text }}>{tr('pflege.media.staged', { count: staged.length })}</span>
            {staged.length > 1 && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                {tr('pflege.media.category_for_all')}
                <select
                  value=""
                  disabled={busy}
                  onChange={e => setCategoryForAll(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">—</option>
                  {categoryOptions(['media', 'document'])}
                </select>
              </label>
            )}
          </div>

          <ul style={{ listStyle: 'none', margin: 0, padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {staged.map(s => (
              <li
                key={s.key}
                data-testid="staged-file"
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: isMobile ? 'wrap' : 'nowrap',
                  padding: '8px 10px', borderRadius: 10,
                  border: `1px solid ${s.status === 'invalid' || s.status === 'error' ? '#fecdca' : t.border}`,
                  background: t.card,
                }}
              >
                {s.previewUrl ? (
                  <img src={s.previewUrl} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                ) : <FileBadge name={s.file.name} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: t.heading, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.file.name}>
                    {s.file.name}
                  </div>
                  <div style={{ fontSize: 11.5, color: s.error ? '#b42318' : t.muted, marginTop: 2 }}>
                    {s.error ?? (
                      s.status === 'uploading'
                        ? tr('pflege.media.status_uploading', { percent: s.progress })
                        : `${formatFileSize(s.file.size)} · ${tr(s.kind === 'media' ? 'pflege.media.kind_media' : 'pflege.media.kind_document')}`
                    )}
                  </div>
                  {s.status === 'uploading' && (
                    <div style={{ height: 4, borderRadius: 4, background: t.borderSoft, marginTop: 6, overflow: 'hidden' }}>
                      <div style={{ width: `${s.progress}%`, height: '100%', background: BRAND, transition: 'width 0.2s' }} />
                    </div>
                  )}
                </div>
                {s.kinds.length > 0 && (
                  <select
                    aria-label={tr('pflege.media.category')}
                    value={`${s.kind}:${s.kategorie}`}
                    disabled={busy}
                    onChange={e => setCategory(s.key, e.target.value)}
                    style={{ ...selectStyle, flexShrink: 0, width: isMobile ? '100%' : 200 }}
                  >
                    {categoryOptions(s.kinds)}
                  </select>
                )}
                <button
                  type="button"
                  aria-label={tr('pflege.media.remove_file')}
                  title={tr('pflege.media.remove_file')}
                  disabled={busy}
                  onClick={() => removeStaged(s.key)}
                  style={{
                    border: 'none', background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer',
                    color: t.muted, fontSize: 18, lineHeight: 1, padding: 4, flexShrink: 0,
                  }}
                >×</button>
              </li>
            ))}
          </ul>

          <div style={{ padding: '12px 16px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="ghost" size="sm" dark={dark} disabled={busy} onClick={clearAll}>
              {tr('pflege.media.discard')}
            </Button>
            <Button variant="primary" size="sm" dark={dark} disabled={busy || readyCount === 0} onClick={() => { void startUpload(); }}>
              {busy ? tr('pflege.media.uploading') : tr('pflege.media.upload_start', { count: readyCount })}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
